<template>
  <div>
    <OnboardingTopBar homepageUrl="/" />
    <LoadingScreen v-if="loading" color="var(--purple)" />
    <div class="container" v-else>
      <div class="illustration">
        <img v-if="platformIllustrationSrc" :src="platformIllustrationSrc" class="platform-icon pulse" />
      </div>
      <div class="title">Login complete. You're all set!</div>
      <div class="subtitle">
        We successfully paired <u>{{ email }}</u> with {{ platformName }}.<br />
        Now head back and start using Anima plugins to create awesome prototypes.
      </div>
      <div class="actions">
        <an-button
          @click="handleOpen"
          v-if="shouldShowGoBackButton"
          variant="purple"
          size="lg"
          :rounded="false"
          class="paired-button-primary"
        >
          Back to {{ platformName }}
        </an-button>
        <an-button @click="navigateToPlayground" variant="empty" size="lg" class="paired-button-secondary">
          Get started
        </an-button>
      </div>
      <div class="footer">
        <span class="footer-text">Not {{ email }}?&nbsp;</span>
        <an-link @click="switchAccount" class="footer-link">Log in</an-link>
        <span class="footer-text">&nbsp;with another account</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import OnboardingTopBar from '@/components/Onboarding/TopBar';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import auth from '@/auth';
import { toastError } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import { uuid } from '@/utils/uuid';
import { sendGoogleConversion } from '@/services/conversion';
import { isExternalRedirect, navigateToExternalRedirect, sanitizeRedirect } from '@/utils/redirects';

export default {
  data() {
    return {
      email: '',
      loading: false,
      platform: '',
      figmaSource: '',
      hasError: false,
      conversionUser: null
    };
  },
  components: {
    LoadingScreen,
    OnboardingTopBar
  },
  mounted() {
    this.applyTheme('dark', 'onboarding');
    this.onMounted();
  },
  computed: {
    ...mapState('users', { user: 'currentItem' }),
    ...mapState('userOnboardings', { userOnboarding: 'currentItem' }),
    ...mapGetters('users', ['firstName', 'lastName']),
    platformIllustrationSrc() {
      if (!['xd', 'sketch', 'figma', 'vscode'].includes(this.platform)) return '';
      return require(`@/assets/illustrations/pair/pair-${this.platform}.svg`);
    },
    platformName() {
      switch (this.platform) {
        case 'xd':
          return 'Adobe XD';
        case 'sketch':
          return 'Sketch';
        case 'figma':
          return 'Figma';
        case 'vscode':
          return 'VSCode';
        default:
          return 'Sketch';
      }
    },
    platformRedirect() {
      switch (this.platform) {
        case 'xd':
          return 'adbxd://';
        case 'sketch':
          return 'sketch://animaapp';
        case 'figma':
          return 'figma://animaapp';
        case 'vscode':
          return 'vscode:extension/animaapp.vscode-anima';
        default:
          return 'figma://animaapp';
      }
    },
    shouldShowGoBackButton() {
      if (this.platform !== 'figma') {
        return true;
      }

      return this.figmaSource === 'desktop';
    }
  },
  methods: {
    ...mapActions({
      getUser: 'users/fetchOne',
      updateUser: 'users/update',
      fetchPairingToken: 'pairingTokens/fetchOne',
      updatePairingToken: 'pairingTokens/update',
      nextOnboardingStage: 'userOnboardings/nextStage',
      fetchUserOnboarding: 'userOnboardings/fetchUserOnboarding'
    }),
    async onMounted() {
      try {
        this.loading = true;
        this.platform = this.$route.query.platform?.toLowerCase();
        this.figmaSource = this.$route.query.figma_source?.toLowerCase();
        this.tempPersonalizedOnboardingAdvance();
        await this.fetchUserData();
        await this.performPairing();
      } catch (error) {
        this.hasError = true;
        errorHandler.captureException(error);
      } finally {
        this.loading = false;
      }
    },
    async tempPersonalizedOnboardingAdvance() {
      const userOnboarding = await this.fetchUserOnboarding();
      if (!userOnboarding) return;

      this.nextOnboardingStage({ currentStageSlug: 'pair-plugin' });
      if (this.platform === 'figma') {
        this.nextOnboardingStage({ currentStageSlug: 'pair-figma' });
      }
    },
    async fetchUserData() {
      const user = await this.getUser({ id: 'me' });
      this.email = user.email;
      this.conversionUser = user;
      if (!localStorage.getItem('animp')) {
        this.$tracking.setUserEmail(user.email);
        this.$tracking.setUserId(user.id);
        this.$tracking.alias(user.id);
      }
    },
    handleOpen() {
      this.$trackEvent('paired.open-platform-link.click', { platform: this.platformName });
      window.location.replace(this.platformRedirect);
    },
    navigateToPlayground() {
      this.$trackEvent('paired.playground.click');
      window.location.href = 'https://dev.animaapp.com';
    },
    navigateToExplore() {
      this.$trackEvent('paired.explore-anima.click');
      this.$router.push({ name: 'learn' }).then(() => this.$router.go());
    },
    async performPairing() {
      const user = await this.getUser({ id: 'me' });
      const { query } = this.$route;
      const { platform, pt: token, intent } = this.$route.query ?? {};

      try {
        const { id } = await this.fetchPairingToken({ id: token, params: { primary_key: 'token' } });
        this.updatePairingToken({ id, payload: { is_paired: true } });
        this.updateUser({ id: 'me', payload: { latest_paired_design_tool: this.platformName } });
        this.$trackEvent('paired.plugin-pairing.success', { platform, ...(intent && { intent }) });

        const transactionId = uuid();

        this.$gtm.trackEvent({
          event: 'paired_plugin',
          event_category: 'paired_plugin',
          event_action: this.platformName,
          event_label: this.user?.role,
          transaction_id: transactionId,
          external_id: this.user?.id,
          first_name: this.user?.first_name,
          last_name: this.user?.last_name,
          email: this.user?.email
        });

        sendGoogleConversion('paired_plugin', transactionId, {
          email: user.email,
          first_name: this.firstName,
          last_name: this.lastName
        });

        if (query.redirect) {
          const redirect = sanitizeRedirect(query.redirect);
          const pairingRedirect = redirect?.includes('pairing=true') ? `${redirect}&after_login=true` : redirect;

          if (isExternalRedirect(pairingRedirect)) {
            navigateToExternalRedirect(pairingRedirect);
            return;
          }

          return this.$router.replace(pairingRedirect || '/', () => {});
        }
      } catch (err) {
        this.$trackEvent('paired.plugin-pairing.failure', { token, platform, message: err.message });
        toastError('Oh no, something went wrong!');
        this.hasError = true;
      }
    },
    switchAccount() {
      const { query } = this.$route;

      this.$trackEvent('paired.switch-account-link.click');

      auth.logout();
      this.$router.replace({ name: 'login', query });
      window.location.reload();
    }
  },
  watch: {
    '$route.query'() {
      this.onMounted();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_fullscreenLayout.scss';
@import '@/styles/_forms.scss';
.container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  max-width: 100%;
}
.illustration {
  height: 230px;
  width: 290px;
  background-image: url('~@/assets/illustrations/pair/pair-dice.svg');
  position: relative;
  margin-top: 100px;

  @media (max-height: 750px) {
    background-position: center;
    background-repeat: no-repeat;
    background-size: 75%;
    height: 140px;
    width: 240px;
    margin-top: 20px;
    transition: all 0.3s;
  }

  .platform-icon {
    height: 60px;
    width: 68px;
    position: absolute;
    top: 0;
    right: 10px;
  }
}
.title {
  @include headline;
  font-weight: 400;
}

// Actions container for buttons
.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin-top: 32px;
}

// Primary button (Back to platform) - Purple with auth flow styling
::v-deep .paired-button-primary {
  width: auto;
  min-width: 200px;
  max-width: 280px;
  justify-content: center;
  border-radius: 8px;
  padding: 0 32px;

  &:focus,
  &:active,
  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
}

// Secondary button (Back to X) - White text, transparent bg, underline
::v-deep .paired-button-secondary {
  width: 100%;
  justify-content: center;
  color: var(--white) !important;
  text-decoration: underline;
  text-underline-offset: 4px;
  font-size: 14px;
  font-weight: 400;
  transition: color 0.2s ease;

  &:hover {
    color: var(--transparent-white-80) !important;
  }

  &:focus,
  &:active,
  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
}

.footer {
  position: fixed;
  bottom: 40px;
  left: 0;
  right: 0;

  @media (max-height: 750px) {
    background: var(--dark-onboarding-background);
    bottom: 0px;
    padding: 10px 20px 30px 20px;
  }

  .footer-text {
    color: var(--transparent-white-50);
  }

  ::v-deep .footer-link {
    color: var(--white) !important;
    font-family: 'Mulish', sans-serif;
    font-size: 14px;
    font-weight: 400;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-80) !important;
      font-weight: 400 !important;
    }
  }
}

@keyframes pulse {
  from {
    transform: scale3d(1, 1, 1);
    opacity: 0;
  }
  50% {
    transform: scale3d(2, 2, 2);
  }
  to {
    transform: scale3d(1, 1, 1);
    opacity: 1;
  }
}
.pulse {
  animation: pulse both linear 0.3s;
  animation-delay: 0.2s;
}
</style>
